import { RefObject, useCallback, useEffect, useState } from 'react'

import { HAVEN_TRIGGER_ATTRIBUTE } from '@/features/adviser-chat/components/ChatTrigger'

const GAP = 16

/** Matches the `max-sm:` breakpoint `StickyBottomNav` stacks its buttons at. */
const STACKED_LAYOUT_MAX_WIDTH = 640

export interface HavenTriggerClearance {
  right: number
  bottom: number
}

const NONE: HavenTriggerClearance = { right: 0, bottom: 0 }

/**
 * An element's edges with any transform ignored. The trigger scales on entry
 * and slides under the cursor on hover, so a rect-based read would size the
 * footer's padding off whatever animation frame it caught.
 */
const untransformedEdgesOf = (el: Element): { left: number; top: number } => {
  const node = el as HTMLElement
  const parent = node.offsetParent
  if (!parent) {
    const box = node.getBoundingClientRect()
    return { left: box.left, top: box.top }
  }
  const parentBox = parent.getBoundingClientRect()
  return {
    left: parentBox.left + node.offsetLeft,
    top: parentBox.top + node.offsetTop,
  }
}

/**
 * How far a fixed footer must hold its content back to clear Haven's floating trigger, zero
 * on both axes when they don't collide. Which axis gives depends on the layout: wide, the
 * buttons inset from the footer's end; stacked, the footer lifts them above the trigger.
 */
export const useHavenTriggerClearance = (
  footerRef: RefObject<HTMLElement>,
  /**
   * The bar's current offsets. It is centred inside the band between them and capped by its
   * own max-width, so changing either moves its right edge without changing its size, and a
   * ResizeObserver alone would not notice.
   */
  offsets: { left?: number; right?: number } = {}
): HavenTriggerClearance => {
  const [clearance, setClearance] = useState<HavenTriggerClearance>(NONE)

  const measure = useCallback(() => {
    const footer = footerRef.current
    const trigger = document.querySelector(`[${HAVEN_TRIGGER_ATTRIBUTE}]`)
    const next = ((): HavenTriggerClearance => {
      if (!footer || !trigger) return NONE
      // The border box, which padding sits inside of, so this cannot feed back into itself.
      const box = footer.getBoundingClientRect()
      const edges = untransformedEdgesOf(trigger)
      return window.innerWidth < STACKED_LAYOUT_MAX_WIDTH
        ? {
            right: 0,
            bottom: Math.max(0, Math.round(box.bottom - edges.top + GAP)),
          }
        : {
            right: Math.max(0, Math.round(box.right - edges.left + GAP)),
            bottom: 0,
          }
    })()
    // Radix portals mutate <body> constantly, so a fresh object each time would re-render
    // the footer for nothing.
    setClearance((prev) =>
      prev.right === next.right && prev.bottom === next.bottom ? prev : next
    )
  }, [footerRef])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)

    // The bar is a centred box, not one pinned to the viewport edge, so the band narrowing
    // moves the edge the trigger is measured against.
    const barObserver = new ResizeObserver(() => measure())
    if (footerRef.current) barObserver.observe(footerRef.current)

    // Each notification costs a document-wide query plus a layout read, so coalesce.
    let scheduled = 0
    const scheduleMeasure = () => {
      if (scheduled) return
      scheduled = window.requestAnimationFrame(() => {
        scheduled = 0
        measure()
      })
    }

    // The trigger portals into <body> and can mount after this footer.
    const observer = new MutationObserver(scheduleMeasure)
    observer.observe(document.body, { childList: true })
    return () => {
      window.removeEventListener('resize', measure)
      barObserver.disconnect()
      observer.disconnect()
      if (scheduled) window.cancelAnimationFrame(scheduled)
    }
  }, [measure, footerRef, offsets.left, offsets.right])

  return clearance
}
